import { ParsedUrlQuery } from 'querystring';
import { Redirect } from 'next';
import { Query } from '../../types/v2/holiday-search';
import { JourneyStep } from '../../types/journey-steps';

export const getPreviousSteps = (journeySteps: JourneyStep[], currentStep: JourneyStep) =>
  journeySteps.filter(({ fields }, index) => fields && index < journeySteps.indexOf(currentStep));

export const getFieldsBeforeCurrentStep = (journeySteps: JourneyStep[], currentStep: JourneyStep) =>
  journeySteps
    .filter(({ fields }, index) => fields && index < journeySteps.indexOf(currentStep))
    .flatMap(({ fields }) => fields);

export const getFieldsAfterStep = (journeySteps: JourneyStep[], step: JourneyStep) =>
  journeySteps
    .filter(({ fields }, index) => fields && index >= journeySteps.indexOf(step))
    .flatMap(({ fields }) => fields);

export const isAfterStep = (
  journeySteps: JourneyStep[],
  currentStep?: JourneyStep,
  comparisonStep?: JourneyStep,
) => {
  if (!currentStep || !comparisonStep) return undefined;

  return journeySteps.indexOf(currentStep) > journeySteps.indexOf(comparisonStep);
};

// ensure that the most specific pathname is used e.g /search-results/holidays/details will return itself instead of matching /search-results/holidays
export const getStep = (journeySteps: JourneyStep[], url?: string | null, field?: string) =>
  journeySteps.reduce(
    (prev, curr) => {
      const urlMatches = url?.includes(curr.pathName);
      if (urlMatches) {
        const urlSplit = url?.split('/');
        const prevMatches =
          urlSplit?.filter((pages) => prev.pathName.split('/').includes(pages)).length || 0;
        const currMatches =
          urlSplit?.filter((pages) => curr.pathName.split('/').includes(pages)).length || 0;
        if (currMatches > prevMatches) return curr;
      }

      const fieldMatches = field ? curr.fields?.some(({ name }) => name === field) : false;

      if (fieldMatches) {
        return curr;
      }

      return prev;
    },
    { pathName: '' },
  );

export const getReferringStepDetails = (
  journeySteps: JourneyStep[],
  currentUrl?: string | null,
  referrerName?: string,
) => {
  const referringStepPathname =
    journeySteps.find((step) => step.name?.toLowerCase() === referrerName?.toLowerCase())
      ?.pathName || '';

  const newReferringStepName = getStep(journeySteps, currentUrl)?.name?.toLowerCase() || '';

  return { referringStepPathname, newReferringStepName };
};

export const getNextStepPathname = (
  journeySteps: JourneyStep[],
  currentUrl: string | null,
  referer?: string | null,
  forcePrevious = false,
): string | undefined => {
  const currentStep = getStep(journeySteps, currentUrl);
  const currentIndex = journeySteps.indexOf(currentStep);
  if (currentIndex === -1) return journeySteps[0].pathName;

  if (forcePrevious) return journeySteps[currentIndex - 1].pathName;

  const refererStep = getStep(journeySteps, referer);
  const refererIndex = journeySteps.indexOf(refererStep);

  if (refererIndex !== -1 && refererIndex > currentIndex) return undefined;

  const newIndex = currentIndex - refererIndex < 0 ? currentIndex - 1 : currentIndex + 1;

  return journeySteps[newIndex]?.pathName || journeySteps[0].pathName;
};

export const getPreviousStepWithoutSelection = (
  journeySteps: JourneyStep[],
  currentPathname: string,
  query: ParsedUrlQuery,
) => {
  const currentStep = getStep(journeySteps, currentPathname);
  const previousSelections = getFieldsBeforeCurrentStep(journeySteps, currentStep);
  const previousSelectionsNotSet = previousSelections.filter((selection) => {
    const isOptional = selection?.optional;
    if (isOptional) return false;

    const selectedFieldName = selection && (query[selection.name] as string);
    const isSingleSelect = !selection?.multiSelect;
    const selectionIsCorrectType = isSingleSelect
      ? !selectedFieldName?.includes(',')
      : !!selectedFieldName;
    return selection && (!selectedFieldName || !selectionIsCorrectType);
  });

  if (previousSelectionsNotSet.length) {
    const firstMissingSelection = previousSelectionsNotSet[0];
    return {
      previousSelectionsMissing: true,
      stepPathName: getStep(journeySteps, undefined, firstMissingSelection?.name).pathName,
    };
  }

  return {
    previousSelectionsMissing: false,
  };
};

export const shouldSkipStep = (
  journeySteps: JourneyStep[],
  currentPathname: string,
  referer: string,
  queryString: string,
  numberOfResults = 0,
  skipStep = false,
): Redirect | undefined => {
  const pathname = getNextStepPathname(journeySteps, currentPathname, referer, false);

  if (pathname && (numberOfResults === 1 || skipStep))
    return {
      destination: `${pathname}?${queryString}`,
      permanent: false,
    };

  return undefined;
};

export const deleteQueryProperties = (
  query: Query,
  properties: string[],
  excludedProperties?: string[],
) => {
  return Object.keys(query).reduce((acc: Query, key: string) => {
    if (excludedProperties?.includes(key) || !properties.includes(key)) {
      acc[key as keyof Query] = query[key as keyof Query];
    }
    return acc;
  }, {});
};
